import { lazy } from 'react'
import { redirect } from 'react-router-dom'

import {
  ContributorNameDocument,
  ContributorSelectorAllDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import buildUrl from 'modules/core/lib/buildUrl'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const ContributorsRootPage = lazy(
  () => import('modules/project_management/pages/ContributorsRootPage')
)
const ContributorsListPage = lazy(
  () => import('modules/project_management/pages/ContributorsListPage')
)
const ContributorViewPage = lazy(
  () => import('modules/project_management/pages/ContributorsListPage/ContributorViewPage')
)

const MyProjectsOverviewPage = lazy(
  () => import('modules/project_management/pages/MyProjectsPage/OverviewPage')
)
const MyProjects = lazy(() => import('modules/project_management/pages/MyProjectsPage/MyProjects'))

export const contributorsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.projects.contributors.path,
      element: <ContributorsRootPage />,
      handle: {
        meta: () => ({
          title: i18n.t('project_management.meta_data.contributors.title'),
          description: i18n.t('project_management.meta_data.contributors.description'),
          illustration: 'group',
        }),
      },
      children: [
        {
          index: true,
          loader: () => redirect(navigation.projects.contributors.list.path),
        },
        {
          path: navigation.projects.contributors.list.path,
          element: <ContributorsListPage />,
        },
        {
          path: navigation.projects.contributors.contributorId.path,
          element: <ContributorViewPage />,
          handle: {
            resource: true,
            meta: ({ params, watchQuery, apolloClient }) =>
              watchQuery({
                query: ContributorNameDocument,
                fetchPolicy: 'cache-first',
                variables: {
                  id: Number(params['employeeId']),
                },
              }).map(({ data }) => {
                const employee = data.projectManagement.currentContributor.nodes[0]?.employee
                if (!employee) {
                  return { title: '' }
                }
                const employeeName = employee.preferredName || employee.firstName
                const employeeDisplayFullName = getEmployeeDisplayName(employee)

                const previousEmployee = data.projectManagement.previousEmployee.nodes[0]
                const nextEmployee = data.projectManagement.nextEmployee.nodes[0]

                const now = moment()
                const year = now.startOf('week').format('YYYY')
                const month = now.startOf('week').format('MM')
                const day = now.startOf('week').format('DD')

                return {
                  title: employeeDisplayFullName,
                  headerComponentProps: {
                    title: employeeDisplayFullName,
                    avatar: {
                      src: buildUrl(employee.avatar?.url) || undefined,
                      firstName: employeeName,
                      lastName: employee.lastName,
                      type: 'person',
                    },
                    description: employee.referenceContract?.jobTitle ?? '',
                  },
                  siblingsNavigation: {
                    previous: previousEmployee
                      ? {
                          title: previousEmployee.employee?.fullName,
                          url: navigation.projects.contributors.contributorId.overview.fullPath({
                            employeeId: previousEmployee.id,
                          }),
                        }
                      : undefined,
                    next: nextEmployee
                      ? {
                          title: nextEmployee.employee?.fullName,
                          url: navigation.projects.contributors.contributorId.overview.fullPath({
                            employeeId: nextEmployee.id,
                          }),
                        }
                      : undefined,
                    select: {
                      url: (value: string) =>
                        navigation.projects.contributors.contributorId.overview.fullPath({
                          employeeId: value,
                        }),
                      options: async () => {
                        const { data } = await apolloClient.query({
                          query: ContributorSelectorAllDocument,
                          variables: {
                            ids: [],
                          },
                        })
                        return (data.projectManagement.projectContributors || [])
                          .map((contributor) => ({
                            label: contributor.employee?.fullName || '',
                            value: contributor.id.toString(),
                            avatar: {
                              type: 'person' as const,
                              firstName:
                                contributor.employee?.preferredName ||
                                contributor.employee?.firstName,
                              lastName: contributor.employee?.lastName,
                              src: buildUrl(contributor.employee?.avatar?.url) || undefined,
                            },
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label))
                      },
                      value: params['employeeId'],
                    },
                  },
                  subNavigation: [
                    {
                      href: navigation.projects.contributors.contributorId.overview.fullPath({
                        employeeId: employee.id,
                      }),
                      title: i18n.t('project_management.my_projects.tabs.overview'),
                      index: true,
                    },
                    {
                      href: navigation.projects.contributors.contributorId.yearMonthDay.fullPath({
                        employeeId: employee.id,
                        year,
                        month,
                        day,
                      }),
                      title: i18n.t('project_management.my_projects.tabs.tracking'),
                    },
                  ],
                }
              }),
          },
          children: [
            {
              index: true,
              loader: () => redirect(navigation.projects.contributors.contributorId.overview.path),
            },
            {
              path: navigation.projects.contributors.contributorId.overview.path,
              element: <MyProjectsOverviewPage />,
            },
            {
              path: navigation.projects.contributors.contributorId.yearMonthDay.path,
              element: <MyProjects />,
            },
          ],
        },
      ],
    },
  ]
}
